export interface Avatar3DInterface {
  skinColor?: AvatarSkinColorEnum;
  eyeColor?: AvatarEyeColorEnum;
  hairColor?: AvatarHairColorEnum;
  shoes?: AvatarShoesEnum;
  pants?: AvatarPantsEnum; // pants are mandatory except for "Kleid"
  clothing: AvatarClothingEnum;
  face: AvatarFaceEnum;
  hair: AvatarHairEnum;
  jewelry: AvararJewelryEnum[];
  eyes: AvatarEyeEnum;
  nose: AvatarNoseEnum;
  mouth: AvatarMouthEnum;
  glasses?: AvatarGlassesEnum;
  hat?: AvatarHatEnum;
}

export namespace Avatar3DUtil {
  // Convert string to numeric seed
  function stringToSeed(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  }

  // Simple seeded random number generator (mulberry32)
  function seededRandom(seed: number): () => number {
    return function () {
      let t = (seed += 0x6d2b79f5);
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  // Helper function to get random element from array
  function getRandomFromArray<T>(array: T[], random: () => number): T {
    return array[Math.floor(random() * array.length)];
  }

  export function generateRandomAvatar(): Avatar3DInterface {
    const getRandomEnum = <T extends Record<string, string>>(
      enumObj: T
    ): T[keyof T] => {
      const values = Object.values(enumObj) as T[keyof T][];
      return values[Math.floor(Math.random() * values.length)];
    };

    const avatar = {
      skinColor: getRandomEnum(AvatarSkinColorEnum),
      eyeColor: getRandomEnum(AvatarEyeColorEnum),
      hairColor: getRandomEnum(AvatarHairColorEnum),
      shoes: getRandomEnum(AvatarShoesEnum),
      pants: getRandomEnum(AvatarPantsEnum),
      clothing: getRandomEnum(AvatarClothingEnum),
      face: getRandomEnum(AvatarFaceEnum),
      hair: getRandomEnum(AvatarHairEnum),
      jewelry: Math.random() > 0.7 ? [getRandomEnum(AvararJewelryEnum)] : [],
      eyes: getRandomEnum(AvatarEyeEnum),
      nose: getRandomEnum(AvatarNoseEnum),
      mouth: getRandomEnum(AvatarMouthEnum),
      glasses:
        Math.random() > 0.8 ? getRandomEnum(AvatarGlassesEnum) : undefined,
      hat: Math.random() > 0.9 ? getRandomEnum(AvatarHatEnum) : undefined,
    };

    return avatar;
  }

  export function generateMaleAvatar(seed: string): Avatar3DInterface {
    const random = seededRandom(stringToSeed(seed));

    const getRandomEnum = <T extends Record<string, string>>(
      enumObj: T
    ): T[keyof T] => {
      const values = Object.values(enumObj) as T[keyof T][];
      return values[Math.floor(random() * values.length)];
    };

    const clothing = getRandomFromArray(getMaleClothing(), random);
    const needsPants =
      clothing !== AvatarClothingEnum.CLOTHING_7 &&
      clothing !== AvatarClothingEnum.CLOTHING_11;

    const avatar: Avatar3DInterface = {
      skinColor: getRandomEnum(AvatarSkinColorEnum),
      eyeColor: getRandomEnum(AvatarEyeColorEnum),
      hairColor: getRandomEnum(AvatarHairColorEnum),
      shoes: getRandomFromArray(getMaleShoes(), random),
      pants: needsPants ? getRandomFromArray(getMalePants(), random) : undefined,
      clothing: clothing,
      face: getRandomEnum(AvatarFaceEnum),
      hair: getRandomFromArray(getMaleHair(), random),
      jewelry: [], // No jewelry for male avatars
      eyes: AvatarEyeEnum.EYE_M,
      nose: getRandomEnum(AvatarNoseEnum),
      mouth: AvatarMouthEnum.MOUTH_M,
      glasses: random() > 0.8 ? getRandomEnum(AvatarGlassesEnum) : undefined,
      hat: random() > 0.9 ? getRandomEnum(AvatarHatEnum) : undefined,
    };

    return avatar;
  }

  export function generateFemaleAvatar(seed: string): Avatar3DInterface {
    const random = seededRandom(stringToSeed(seed));

    const getRandomEnum = <T extends Record<string, string>>(
      enumObj: T
    ): T[keyof T] => {
      const values = Object.values(enumObj) as T[keyof T][];
      return values[Math.floor(random() * values.length)];
    };

    const clothing = getRandomFromArray(getFemaleClothing(), random);
    const needsPants =
      clothing !== AvatarClothingEnum.CLOTHING_7 &&
      clothing !== AvatarClothingEnum.CLOTHING_11;

    const avatar: Avatar3DInterface = {
      skinColor: getRandomEnum(AvatarSkinColorEnum),
      eyeColor: getRandomEnum(AvatarEyeColorEnum),
      hairColor: getRandomEnum(AvatarHairColorEnum),
      shoes: getRandomFromArray(getFemaleShoes(), random),
      pants: needsPants ? getRandomFromArray(getFemalePants(), random) : undefined,
      clothing: clothing,
      face: getRandomEnum(AvatarFaceEnum),
      hair: getRandomFromArray(getFemaleHair(), random),
      jewelry: random() > 0.7 ? [getRandomEnum(AvararJewelryEnum)] : [],
      eyes: AvatarEyeEnum.EYE_F,
      nose: getRandomEnum(AvatarNoseEnum),
      mouth: AvatarMouthEnum.MOUTH_F,
      glasses: random() > 0.8 ? getRandomEnum(AvatarGlassesEnum) : undefined,
      hat: random() > 0.9 ? getRandomEnum(AvatarHatEnum) : undefined,
    };

    return avatar;
  }

  // Gender-specific clothing helper functions
  export function getMaleClothing(): AvatarClothingEnum[] {
    return [
      AvatarClothingEnum.CLOTHING_1,
      AvatarClothingEnum.CLOTHING_4,
      AvatarClothingEnum.CLOTHING_5,
      AvatarClothingEnum.CLOTHING_6,
      AvatarClothingEnum.CLOTHING_12,
    ];
  }

  export function getFemaleClothing(): AvatarClothingEnum[] {
    return [
      AvatarClothingEnum.CLOTHING_1,
      AvatarClothingEnum.CLOTHING_2,
      AvatarClothingEnum.CLOTHING_3,
      AvatarClothingEnum.CLOTHING_7,
      AvatarClothingEnum.CLOTHING_8,
      AvatarClothingEnum.CLOTHING_9,
      AvatarClothingEnum.CLOTHING_10,
      AvatarClothingEnum.CLOTHING_11,
      AvatarClothingEnum.CLOTHING_12,
    ];
  }

  export function getMalePants(): AvatarPantsEnum[] {
    return [
      AvatarPantsEnum.PANTS_1,
      AvatarPantsEnum.PANTS_5,
      AvatarPantsEnum.PANTS_6,
    ];
  }

  export function getFemalePants(): AvatarPantsEnum[] {
    return [
      AvatarPantsEnum.PANTS_2,
      AvatarPantsEnum.PANTS_3,
      AvatarPantsEnum.PANTS_4,
      AvatarPantsEnum.PANTS_5,
    ];
  }

  export function getMaleShoes(): AvatarShoesEnum[] {
    return [
      AvatarShoesEnum.SHOE_2,
      AvatarShoesEnum.SHOE_4,
      AvatarShoesEnum.SHOE_5,
      AvatarShoesEnum.SHOE_6,
      AvatarShoesEnum.SHOE_7,
    ];
  }

  export function getFemaleShoes(): AvatarShoesEnum[] {
    return [
      AvatarShoesEnum.SHOE_1,
      AvatarShoesEnum.SHOE_2,
      AvatarShoesEnum.SHOE_3,
      AvatarShoesEnum.SHOE_4,
      AvatarShoesEnum.SHOE_5,
      AvatarShoesEnum.SHOE_6,
      AvatarShoesEnum.SHOE_7,
    ];
  }

  export function getMaleHair(): AvatarHairEnum[] {
    return [
      AvatarHairEnum.NONE,
      AvatarHairEnum.HAIR_3,
      AvatarHairEnum.HAIR_4,
      AvatarHairEnum.HAIR_5,
      AvatarHairEnum.HAIR_6,
      AvatarHairEnum.HAIR_7,
    ];
  }

  export function getFemaleHair(): AvatarHairEnum[] {
    return [
      AvatarHairEnum.NONE,
      AvatarHairEnum.HAIR_1,
      AvatarHairEnum.HAIR_2,
      AvatarHairEnum.HAIR_3,
      AvatarHairEnum.HAIR_4,
      AvatarHairEnum.HAIR_5,
      AvatarHairEnum.HAIR_6,
      AvatarHairEnum.HAIR_7,
      AvatarHairEnum.HAIR_8,
      AvatarHairEnum.HAIR_9,
      AvatarHairEnum.HAIR_10,
    ];
  }
}

export type AvatarFeatureType =
  | AvatarShoesEnum
  | AvatarPantsEnum
  | AvatarClothingEnum
  | AvatarFaceEnum
  | AvatarHairEnum
  | AvararJewelryEnum
  | AvatarEyeEnum
  | AvatarNoseEnum
  | AvatarMouthEnum
  | AvatarGlassesEnum
  | AvatarHatEnum;

export enum AvatarShoesEnum {
  SHOE_1 = 'Shoe_x5F_01',
  SHOE_2 = 'Shoe_x5F_02',
  SHOE_3 = 'Shoe_x5F_03',
  SHOE_4 = 'Shoe_x5F_04',
  SHOE_5 = 'Shoe_x5F_05',
  SHOE_6 = 'Shoe_x5F_06',
  SHOE_7 = 'Shoe_x5F_07',
}

export enum AvatarPantsEnum {
  PANTS_1 = 'Pants_x5F_01',
  PANTS_2 = 'Pants_x5F_02',
  PANTS_3 = 'Pants_x5F_03',
  PANTS_4 = 'Pants_x5F_04',
  PANTS_5 = 'Pants_x5F_05',
  PANTS_6 = 'Pants_x5F_06',
}

export enum AvatarClothingEnum {
  CLOTHING_1 = 'Clothing_x5F_01', // also needs Clothing_x5F_hand_x5F_01
  CLOTHING_2 = 'Clothing_x5F_02', // also needs Clothing_x5F_hand_x5F_02
  CLOTHING_3 = 'Clothing_x5F_03', // also needs Clothing_x5F_hand_x5F_03
  CLOTHING_4 = 'Clothing_x5F_04', // also needs Clothing_x5F_hand_x5F_04
  CLOTHING_5 = 'Clothing_x5F_05', // also needs Clothing_x5F_hand_x5F_05
  CLOTHING_6 = 'Clothing_x5F_06',
  CLOTHING_7 = 'Clothing_x5F_07', // "Kleid" - so no pants with this!
  CLOTHING_8 = 'Clothing_x5F_08',
  CLOTHING_9 = 'Clothing_x5F_09',
  CLOTHING_10 = 'Clothing_x5F_10',
  CLOTHING_11 = 'Clothing_x5F_11', // "Kleid" - so no pants with this!
  CLOTHING_12 = 'Clothing_x5F_12',
}

export enum AvatarFaceEnum {
  FACE_1 = 'Face_x5F_Base_x5F_01',
  FACE_2 = 'Face_x5F_Base_x5F_02',
  FACE_3 = 'Face_x5F_Base_x5F_03',
}

export enum AvararJewelryEnum {
  JEWELRY_1 = 'jewelry_x5F_01',
  JEWELRY_2 = 'jewelry_x5F_02',
  JEWELRY_3 = 'jewelry_x5F_03',
}

export enum AvatarEyeEnum {
  EYE_F = 'F_x5F_Eye',
  EYE_M = 'M_x5F_Eye',
}

export enum AvatarNoseEnum {
  NOSE_1 = 'Nose_x5F_01',
  NOSE_2 = 'Nose_x5F_02',
  NOSE_3 = 'Nose_x5F_03',
  NOSE_4 = 'Nose_x5F_04',
  NOSE_5 = 'Nose_x5F_05',
}

export enum AvatarMouthEnum {
  MOUTH_F = 'F_x5F_Mouth',
  MOUTH_M = 'M_x5F_Mouth',
}

export enum AvatarGlassesEnum {
  GLASSES_1 = 'Glasses_x5F_01',
  GLASSES_2 = 'Glasses_x5F_02',
  GLASSES_3 = 'Glasses_x5F_03',
  GLASSES_4 = 'Glasses_x5F_04',
  GLASSES_5 = 'Glasses_x5F_05',
}

export enum AvatarHairEnum {
  NONE = 'none',
  HAIR_1 = 'hair1_x5F_front', // also needs Hair1_x5F_back
  HAIR_2 = 'hair2_x5F_front', // also needs Hair2_x5F_back
  HAIR_3 = 'hair_x5F_03',
  HAIR_4 = 'hair_x5F_04',
  HAIR_5 = 'hair_x5F_05',
  HAIR_6 = 'hair_x5F_06',
  HAIR_7 = 'hair_x5F_07',
  HAIR_8 = 'hair_x5F_08',
  HAIR_9 = 'hair_x5F_09',
  HAIR_10 = 'hair_x5F_10',
}

export enum AvatarHatEnum {
  HAT_1 = 'Hat_x5F_01',
  HAT_2 = 'Hat_x5F_02',
  HAT_3 = 'Hat_x5F_03',
  HAT_4 = 'Hat_x5F_04',
  HAT_5 = 'Hat_x5F_05',
}

export enum AvatarSkinColorEnum {
  COLOR_1 = 'COLOR_1',
  COLOR_2 = 'COLOR_2',
  COLOR_3 = 'COLOR_3',
  COLOR_4 = 'COLOR_4',
  COLOR_5 = 'COLOR_5',
  COLOR_6 = 'COLOR_6',
  COLOR_7 = 'COLOR_7',
}

export namespace AvatarSkinColorUtil {
  const SKIN_COLORS: Record<
    AvatarSkinColorEnum,
    { base: string; shadow: string; stroke: string }
  > = {
    [AvatarSkinColorEnum.COLOR_1]: {
      base: '#f8e8d9',
      shadow: '#e3b19f',
      stroke: '#e3b19f',
    },
    [AvatarSkinColorEnum.COLOR_2]: {
      base: '#f0d4be',
      shadow: '#d1a88a',
      stroke: '#d1a88a',
    },
    [AvatarSkinColorEnum.COLOR_3]: {
      base: '#fdd8a2',
      shadow: '#caad82',
      stroke: '#caad82',
    },
    [AvatarSkinColorEnum.COLOR_4]: {
      base: '#efc088',
      shadow: '#bf9a6d',
      stroke: '#bf9a6d',
    },
    [AvatarSkinColorEnum.COLOR_5]: {
      base: '#d39171',
      shadow: '#a9745a',
      stroke: '#a9745a',
    },
    [AvatarSkinColorEnum.COLOR_6]: {
      base: '#885633',
      shadow: '#6d4529',
      stroke: '#6d4529',
    },
    [AvatarSkinColorEnum.COLOR_7]: {
      base: '#633a17',
      shadow: '#4f2e12',
      stroke: '#4f2e12',
    },
  };

  export function getSkinColorBase(color: AvatarSkinColorEnum): string {
    return SKIN_COLORS[color].base;
  }

  export function getSkinColorShadow(color: AvatarSkinColorEnum): string {
    return SKIN_COLORS[color].shadow;
  }

  export function getSkinColorStroke(color: AvatarSkinColorEnum): string {
    return SKIN_COLORS[color].stroke;
  }
}

export enum AvatarEyeColorEnum {
  LIGHT_BLUE = 'LIGHT_BLUE',
  DARK_BLUE = 'DARK_BLUE',
  GREEN = 'GREEN',
  HAZEL = 'HAZEL',
  BROWN = 'BROWN',
  GRAY = 'GRAY',
}

export namespace AvatarEyeColorUtil {
  const EYE_COLORS: Record<
    AvatarEyeColorEnum,
    { name: string; value: string; shadow: string }
  > = {
    [AvatarEyeColorEnum.LIGHT_BLUE]: {
      name: 'Hellblau',
      value: '#7fb3ff',
      shadow: '#5a8cd9',
    },
    [AvatarEyeColorEnum.DARK_BLUE]: {
      name: 'Dunkelblau',
      value: '#325f9c',
      shadow: '#24456e',
    },
    [AvatarEyeColorEnum.GREEN]: {
      name: 'Grün',
      value: '#4f8b3b',
      shadow: '#3a682c',
    },
    [AvatarEyeColorEnum.HAZEL]: {
      name: 'Hasel',
      value: '#8a613a',
      shadow: '#66472a',
    },
    [AvatarEyeColorEnum.BROWN]: {
      name: 'Braun',
      value: '#5a3a22',
      shadow: '#3f2717',
    },
    [AvatarEyeColorEnum.GRAY]: {
      name: 'Grau',
      value: '#9da7b5',
      shadow: '#727e8c',
    },
  };

  export function getEyeColorValue(color: AvatarEyeColorEnum): string {
    return EYE_COLORS[color].value;
  }

  export function getEyeColorShadow(color: AvatarEyeColorEnum): string {
    return EYE_COLORS[color].shadow;
  }

  export function getEyeColorLabel(color: AvatarEyeColorEnum): string {
    return EYE_COLORS[color].name;
  }
}

export enum AvatarHairColorEnum {
  BLACK = 'BLACK',
  DARK_BROWN = 'DARK_BROWN',
  MEDIUM_BROWN = 'MEDIUM_BROWN',
  LIGHT_BROWN = 'LIGHT_BROWN',
  GOLDEN_BLONDE = 'GOLDEN_BLONDE',
  ASH_BLONDE = 'ASH_BLONDE',
  COPPER_RED = 'COPPER_RED',
  INTENSE_RED = 'INTENSE_RED',
  VIOLET = 'VIOLET',
  PINK = 'PINK',
  TURQUOISE = 'TURQUOISE',
  BLUE = 'BLUE',
  NEON_GREEN = 'NEON_GREEN',
  PASTEL_PURPLE = 'PASTEL_PURPLE',
  SILVER_WHITE = 'SILVER_WHITE',
}

export namespace AvatarHairColorUtil {
  const HAIR_COLORS: Record<
    AvatarHairColorEnum,
    {
      name: string;
      base: string;
      shadow: string;
      accent: string;
      stroke: string;
    }
  > = {
    [AvatarHairColorEnum.BLACK]: {
      name: 'Schwarz',
      base: '#141318',
      shadow: '#09080b',
      accent: '#18171d',
      stroke: '#050406',
    },
    [AvatarHairColorEnum.DARK_BROWN]: {
      name: 'Dunkelbraun',
      base: '#4a2f1a',
      shadow: '#2f1d0e',
      accent: '#52351f',
      stroke: '#1f1209',
    },
    [AvatarHairColorEnum.MEDIUM_BROWN]: {
      name: 'Mittelbraun',
      base: '#6b4426',
      shadow: '#4a2f19',
      accent: '#744a2b',
      stroke: '#321c0e',
    },
    [AvatarHairColorEnum.LIGHT_BROWN]: {
      name: 'Hellbraun',
      base: '#8a5a33',
      shadow: '#603c21',
      accent: '#935f37',
      stroke: '#3a2313',
    },
    [AvatarHairColorEnum.GOLDEN_BLONDE]: {
      name: 'Goldblond',
      base: '#d7ae5b',
      shadow: '#a17e3f',
      accent: '#e0b663',
      stroke: '#6c542b',
    },
    [AvatarHairColorEnum.ASH_BLONDE]: {
      name: 'Aschblond',
      base: '#c8b792',
      shadow: '#9b8a68',
      accent: '#d0c09a',
      stroke: '#6b5f45',
    },
    [AvatarHairColorEnum.COPPER_RED]: {
      name: 'Kupferrot',
      base: '#c45432',
      shadow: '#8e3b22',
      accent: '#cc5c38',
      stroke: '#5a2616',
    },
    [AvatarHairColorEnum.INTENSE_RED]: {
      name: 'Intensivrot',
      base: '#e03b3b',
      shadow: '#aa2525',
      accent: '#e64848',
      stroke: '#6a1515',
    },
    [AvatarHairColorEnum.VIOLET]: {
      name: 'Violett',
      base: '#7a3bb9',
      shadow: '#4e247c',
      accent: '#8442c4',
      stroke: '#331554',
    },
    [AvatarHairColorEnum.PINK]: {
      name: 'Pink',
      base: '#ff6ca9',
      shadow: '#c04676',
      accent: '#ff78b0',
      stroke: '#7f2c4c',
    },
    [AvatarHairColorEnum.TURQUOISE]: {
      name: 'Türkis',
      base: '#26bfb5',
      shadow: '#18817a',
      accent: '#2ccbc0',
      stroke: '#0f514c',
    },
    [AvatarHairColorEnum.BLUE]: {
      name: 'Blau',
      base: '#3a6bff',
      shadow: '#2540aa',
      accent: '#4676ff',
      stroke: '#162664',
    },
    [AvatarHairColorEnum.NEON_GREEN]: {
      name: 'Neongrün',
      base: '#56e061',
      shadow: '#2d9a35',
      accent: '#63ea6e',
      stroke: '#1a5c20',
    },
    [AvatarHairColorEnum.PASTEL_PURPLE]: {
      name: 'Pastelllila',
      base: '#c6a5ff',
      shadow: '#9277c2',
      accent: '#d0b3ff',
      stroke: '#604a86',
    },
    [AvatarHairColorEnum.SILVER_WHITE]: {
      name: 'Silberweiß',
      base: '#e6e6f0',
      shadow: '#b0b0c2',
      accent: '#f0f0fa',
      stroke: '#7a7a8c',
    },
  };

  export function getHairColorBase(color: AvatarHairColorEnum): string {
    return HAIR_COLORS[color].base;
  }

  export function getHairColorShadow(color: AvatarHairColorEnum): string {
    return HAIR_COLORS[color].shadow;
  }

  export function getHairColorAccent(color: AvatarHairColorEnum): string {
    return HAIR_COLORS[color].accent;
  }

  export function getHairColorStroke(color: AvatarHairColorEnum): string {
    return HAIR_COLORS[color].stroke;
  }

  export function getHairColorLabel(color: AvatarHairColorEnum): string {
    return HAIR_COLORS[color].name;
  }
}
